import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";

import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Stack } from "../../../components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import syntaxTheme from "../../../shared-components/syntaxTheme";
import syntaxHighlighterStyle from "../../../shared-components/syntaxHighlighterStyle";

import { Search, EllipsisVertical, Download } from "lucide-react";
import { TimeRange } from "../types";
import {
  useClusterLogs,
  useClusterInfraEvents,
  previousTimeInterval,
} from "../../../crud/logs/hooks";
import { formatInTimeZone } from "date-fns-tz";
import {
  BooleanParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";
import { useDebounce, useTimezone } from "../../../utils/hooks";
import { timeRangeToString } from "./utils";
import { LogEntry } from "../../../crud/logs/types";
import { LogsHistogram } from "./charts/LogsHistogram";
import { BackendTypesEnum, ClusterFrontendSchema } from "../../../api-client";
import { CodeBlock } from "../../../shared-components/CodeBlock";
import { BadgeWithClose } from "../../../components/system/badge-with-close";

import {
  AllCommunityModule,
  ModuleRegistry,
  themeBalham,
  ColDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

import { getStateColorClass } from "./badge-colors";

export const InstanceLogsView = ({
  clusterId,
  cluster,
  instanceId,
  timeRange,
}: {
  clusterId: number;
  cluster: ClusterFrontendSchema;
  instanceId: number;
  timeRange?: TimeRange;
}): React.ReactElement => {
  const clusterIsRunning = cluster?.finishedAt === undefined;

  return (
    <LogsView
      clusterId={clusterId.toString()}
      cluster={cluster}
      instanceIds={[instanceId]}
      timeRange={timeRange}
      defaultMetricsTimeRange={timeRange}
      defaultLogTimeRange={timeRange}
      setTimeRange={(t) => {}}
      refAreaLeft={0}
      refAreaRight={1}
      setRefAreaLeft={(n) => {}}
      setRefAreaRight={(n) => {}}
      zoomOut={() => {}}
      clusterIsRunning={clusterIsRunning}
      showHistogram={false}
      showLifecycle={false}
      showInstanceColumn={false}
    />
  );
};

export const LogsView = ({
  clusterId,
  cluster,
  timeRange,
  setTimeRange,
  defaultLogTimeRange,
  defaultMetricsTimeRange,
  refAreaLeft,
  refAreaRight,
  setRefAreaLeft,
  setRefAreaRight,
  clusterIsRunning,
  zoomOut,
  instanceIds = undefined,
  showHistogram = true,
  showLifecycle = undefined,
  highlightTasks = false,
  showInstanceColumn = true,
  showTaskColumn = undefined,
}: {
  clusterId: string;
  cluster?: ClusterFrontendSchema;
  instanceIds?: number[];
  timeRange?: TimeRange;
  setTimeRange: (timeRange: TimeRange | undefined) => void;
  defaultLogTimeRange?: TimeRange;
  defaultMetricsTimeRange?: TimeRange;
  refAreaLeft: number;
  refAreaRight: number;
  setRefAreaLeft: (refAreaLeft: number) => void;
  setRefAreaRight: (refAreaRight: number) => void;
  clusterIsRunning?: boolean;
  zoomOut: () => void;
  showHistogram?: boolean;
  showLifecycle?: boolean;
  highlightTasks?: boolean;
  showInstanceColumn?: boolean;
  showTaskColumn?: boolean;
}): React.ReactElement => {
  const liveUpdates = timeRange === defaultMetricsTimeRange && clusterIsRunning;
  const gridRef = useRef<AgGridReact>(null);

  const [highlightInstance, setHighlightInstance] = useState(-1);
  const [highlightTask, setHighlightTask] = useState(-1);
  const [logHistoryComplete, setLogHistoryComplete] = useState(false);
  const [earliestTimestamp, setEarliestTimestamp] = useState<number>();

  const [displayTz] = useTimezone();

  const cloudProvider = cluster?.backendType;
  const canFetchClusterLogs = cloudProvider !== BackendTypesEnum.VmAzure;
  const [dask, setDask] = useQueryParam(
    "daskLogs",
    withDefault(BooleanParam, canFetchClusterLogs),
  );
  const [system, setSystem] = useQueryParam(
    "systemLogs",
    withDefault(BooleanParam, false),
  );

  const [uiSwitchShowLifecycle, setUiSwitchShowLifecycle] = useQueryParam(
    "showLifecycle",
    withDefault(BooleanParam, true),
  );

  const [filterTimeRange, setFilterTimeRange] = useQueryParam(
    "filterLogTime",
    withDefault(BooleanParam, true),
  );

  const [filterPattern, setFilterPattern] = useQueryParam(
    "filterPattern",
    withDefault(StringParam, ""),
  );

  const showInfraEvents = showLifecycle ?? uiSwitchShowLifecycle;

  const instanceLabelMap: Map<number | string, string> = useMemo(() => {
    const labelMap: Map<number | string, string> = new Map(
      cluster?.workersExceptUnprovisionable.map((workerProcess) => {
        return [
          workerProcess?.instance?.id || 0,
          workerProcess?.instance?.privateIpAddress || "",
        ];
      }),
    );
    if (cluster?.scheduler?.instance?.id) {
      labelMap.set(cluster.scheduler.instance.id, "scheduler");
    }

    // set "client" in map so it works for filtering on "instance:client"
    labelMap.set("client", "client");

    return labelMap;
  }, [cluster]);

  const getInstanceLabel = useCallback(
    (instanceId?: number) => {
      return instanceId
        ? instanceLabelMap.get(instanceId) || String(instanceId)
        : "";
    },
    [instanceLabelMap],
  );

  const renderInstanceLabel = useCallback(
    (instanceId: number) => {
      const label = getInstanceLabel(instanceId);
      return (
        <span
          onMouseEnter={() => {
            setHighlightInstance(instanceId);
          }}
          onMouseLeave={() => {
            setHighlightInstance(-1);
          }}
          onClick={() => {
            setFilterPattern(`instance:${label} ${filterPattern} `.trim());
          }}
          className="font-mono text-sm cursor-pointer"
          style={{
            fontFamily: "Roboto Mono, monospace",
            fontSize: "13px",
            textDecoration: "none",
          }}
        >
          {instanceId === highlightInstance ? (
            <b>
              <u>{label}</u>
            </b>
          ) : (
            label
          )}
        </span>
      );
    },
    [
      filterPattern,
      getInstanceLabel,
      setFilterPattern,
      highlightInstance,
      setHighlightInstance,
    ],
  );

  const renderTaskLabel = useCallback(
    (taskId: number | null) => {
      if (taskId === null) {
        return <></>;
      }
      const label = taskId.toString();
      return (
        <span
          onMouseEnter={() => {
            setHighlightTask(taskId);
          }}
          onMouseLeave={() => {
            setHighlightTask(-1);
          }}
          onClick={() => {
            setFilterPattern(`task:${label} ${filterPattern} `.trim());
          }}
          className="font-mono text-sm cursor-pointer"
          style={{
            fontFamily: "Roboto Mono, monospace",
            fontSize: "13px",
            textDecoration: "none",
          }}
        >
          {taskId === highlightTask ? (
            <b>
              <u>{label}</u>
            </b>
          ) : (
            label
          )}
        </span>
      );
    },
    [filterPattern, setFilterPattern, highlightTask, setHighlightTask],
  );

  const taskColumns: ColDef<LogEntry>[] = useMemo(
    () =>
      (showTaskColumn ?? cluster?.clusterType === "batch")
        ? [
            {
              field: "taskId",
              headerName: "Task",
              width: 60,
              cellRenderer: (props: any) => renderTaskLabel(props.value),
              pinned: "left",
            },
          ]
        : [],
    [cluster, showTaskColumn, renderTaskLabel],
  );

  const Columns: ColDef<LogEntry>[] = useMemo(
    () => [
      {
        field: "timestamp",
        headerName: `Time (${displayTz})`,
        valueFormatter: (props: any) =>
          formatInTimeZone(props.value, displayTz, "yyyy-MM-dd HH:mm:ss.SSSS"),
        minWidth: 200,
        pinned: "left",
        initialSort: "desc",
      },
      ...((showInstanceColumn
        ? [
            {
              field: "instanceId",
              headerName: "Instance",
              width: 120,
              // "scheduler" or private IP address for workers
              cellRenderer: (props: any) => renderInstanceLabel(props.value),
              valueFormatter: (props: any) => getInstanceLabel(props.value),
              pinned: "left",
            },
          ]
        : []) as ColDef<LogEntry>[]),
      ...taskColumns,
      {
        field: "message",
        headerName: "Message",
        minWidth: 2000,
        resizable: false,
        autoHeight: true,
        cellRenderer: (props: any) => {
          const message = props.value;
          return (
            <>
              {props.data.state &&
                (props.data.stateExplanation ? (
                  <Tooltip
                    key={`tt${props.data.id}_${props.data.instanceId}_${props.data.taskId}`}
                  >
                    <TooltipTrigger>
                      <div className="inline-flex items-center gap-1">
                        <Badge
                          key={`chip${props.data.id}_${props.data.instanceId}_${props.data.taskId}`}
                          className={getStateColorClass(
                            props.data.stateColor,
                            props.data.stateFilled,
                          )}
                        >
                          {props.data.state}
                        </Badge>
                        {message}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{props.data.stateExplanation}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="inline-flex items-center gap-1">
                    <Badge
                      key={`chip${props.data.id}_${props.data.instanceId}_${props.data.taskId}`}
                      className={getStateColorClass(
                        props.data.stateColor,
                        props.data.stateFilled,
                      )}
                    >
                      {props.data.state}
                    </Badge>
                    {message}
                  </div>
                ))}
              {props.data.logType === "dask" ? (
                <SyntaxHighlighter
                  language="python"
                  style={syntaxTheme}
                  customStyle={syntaxHighlighterStyle}
                  key={`sh${props.data.id}_${props.data.instanceId}_${props.data.taskId}`}
                >
                  {message}
                </SyntaxHighlighter>
              ) : (
                !props.data.state && (
                  <div
                    className="whitespace-nowrap"
                    key={`log${props.data.id}_${props.data.instanceId}_${props.data.taskId}`}
                  >
                    {message}
                  </div>
                )
              )}
            </>
          );
        },
      },
    ],
    [
      displayTz,
      renderInstanceLabel,
      renderTaskLabel,
      getInstanceLabel,
      showInstanceColumn,
      taskColumns,
    ],
  );

  const { data, isFetching, error, fetchPreviousPage } = useClusterLogs(
    clusterId,
    dask,
    system,
    timeRange,
    instanceIds,
    liveUpdates,
  );

  // much faster and less error prone than cluster logs, so for now ignoring fetch and error states
  // TODO add enable so we only fetch if we're going to show
  const { data: infraEventData } = useClusterInfraEvents(
    clusterId,
    defaultLogTimeRange,
  );

  // This effect is responsible for fetching historical log data
  useEffect(() => {
    const startTimes = data?.pages.map((page) => page.timeRange[0]);
    const earliestTimestampInLogs = startTimes && Math.min(...startTimes);

    if (!timeRange || !earliestTimestampInLogs) return;

    const shouldFetchHistory = earliestTimestampInLogs > timeRange[0];

    if (shouldFetchHistory) {
      setEarliestTimestamp(earliestTimestampInLogs);

      fetchPreviousPage({
        pageParam: {
          previousTimeRange: previousTimeInterval([earliestTimestampInLogs, 0]),
        },
      });
    } else {
      // We've reached the end of the logs history
      setLogHistoryComplete(true);
    }
  }, [data?.pages, fetchPreviousPage, timeRange]);

  const logs = useMemo(() => {
    const flatLogs = (data?.pages?.flatMap((page) => page?.data) || []).concat(
      (showInfraEvents && infraEventData) || [],
    );

    const filteredLogs = filterLogs(
      flatLogs,
      instanceLabelMap,
      filterPattern,
      filterTimeRange,
      timeRange,
    );

    const dedupedLogs = dedupeLogs(filteredLogs);

    return dedupedLogs;
  }, [
    data?.pages,
    infraEventData,
    showLifecycle,
    showInfraEvents,
    filterPattern,
    timeRange,
    filterTimeRange,
    instanceLabelMap,
  ]);

  const hasZoomed = timeRange !== defaultMetricsTimeRange;
  const showFilterAlert = filterPattern && !logHistoryComplete;

  const filterAlertRef = useRef<HTMLDivElement>(null);
  const filterAlertHeight = filterAlertRef.current
    ? filterAlertRef.current.clientHeight + 8 // 8px for spacing
    : 0;

  const zoomMessageRef = useRef<HTMLDivElement>(null);
  const zoomMessageHeight = zoomMessageRef.current
    ? zoomMessageRef.current.clientHeight + 8 // 8px for spacing
    : 0;

  // DataGridPro needs us to manually set the height of the grid container
  // so that virtualization works properly
  const topHeight = 220 + filterAlertHeight + zoomMessageHeight;

  const gridTheme = themeBalham.withParams({
    cellFontFamily: "Roboto Mono, monospace",
    rowBorder: { style: "none" },
    spacing: 2,
  });

  return (
    <div className="h-full pb-0">
      <Stack className="h-full relative">
        {cloudProvider === BackendTypesEnum.VmAzure && (
          <Alert>
            We don’t yet support viewing Azure cluster logs in the UI. You can
            see logs using the CLI:
            <CodeBlock
              language="bash"
              snippet={`coiled cluster azure-logs --cluster ${clusterId}`}
            />
          </Alert>
        )}
        <div className="flex flex-row items-center gap-4 mb-1">
          <FilterField
            filterPattern={filterPattern}
            setFilterPattern={setFilterPattern}
          />

          <div className="flex items-center space-x-2">
            <Switch
              id="dask-logs"
              checked={dask}
              onCheckedChange={() => setDask(!dask)}
              disabled={!canFetchClusterLogs}
            />
            <Label htmlFor="dask-logs">Application</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="system-logs"
              checked={system}
              onCheckedChange={() => setSystem(!system)}
              disabled={!canFetchClusterLogs}
            />
            <Label htmlFor="system-logs">System</Label>
          </div>
          {showLifecycle === undefined && (
            <div className="flex items-center space-x-2">
              <Switch
                id="lifecycle-logs"
                checked={uiSwitchShowLifecycle}
                onCheckedChange={() =>
                  setUiSwitchShowLifecycle(!uiSwitchShowLifecycle)
                }
              />
              <Label htmlFor="lifecycle-logs">Lifecycle</Label>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="filter-time-range"
                    checked={filterTimeRange}
                    onCheckedChange={() => setFilterTimeRange(!filterTimeRange)}
                  />
                  <Label htmlFor="filter-time-range">
                    Filter by selected time
                  </Label>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {error ? (
          <Alert variant="destructive" className="whitespace-pre-wrap">
            {error?.message}
          </Alert>
        ) : null}
        {showFilterAlert ? (
          <Alert ref={filterAlertRef}>
            Filtered results can be sparse while logs are loading.
          </Alert>
        ) : null}
        {showHistogram && (
          <LogsHistogram
            logs={logs}
            timeRange={timeRange}
            setTimeRange={setTimeRange}
            defaultTimeRange={
              // Charts only show the "No Data" message if the user hasn't zoomed (timeRange != defaultTimeRange), let's
              // also make that check fail when a filter pattern has been entered to avoid showing the "No Data" message
              filterPattern ? undefined : defaultMetricsTimeRange
            }
            refAreaLeft={refAreaLeft}
            refAreaRight={refAreaRight}
            setRefAreaLeft={setRefAreaLeft}
            setRefAreaRight={setRefAreaRight}
          />
        )}
        {timeRange && hasZoomed && filterTimeRange && (
          <div className="pl-1" ref={zoomMessageRef}>
            <span>Showing logs between</span>{" "}
            <BadgeWithClose
              variant="outline"
              onClose={zoomOut}
              className="ml-1"
            >
              {timeRangeToString(timeRange)}
            </BadgeWithClose>
          </div>
        )}
        <div
          className="relative rounded-lg"
          style={{
            height: `calc(100vh - ${topHeight}px)`,
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
          }}
        >
          <div className="absolute top-0 right-0 pt-0 z-30 rounded-lg">
            {isFetching || !logHistoryComplete ? (
              <div className="inline-block w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            ) : null}
            {!logHistoryComplete && earliestTimestamp ? (
              <div className="inline-block ml-1">
                <span style={{ fontStyle: "italic" }}>
                  Loading logs prior to{" "}
                </span>
                <Badge variant="outline">
                  {formatInTimeZone(
                    new Date(earliestTimestamp),
                    displayTz,
                    "LLL do, HH:mm:ss",
                  )}
                </Badge>
              </div>
            ) : null}
            {timeRange && cluster ? (
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => gridRef.current!.api.exportDataAsCsv()}
                    className="p-1 h-6 w-6"
                  >
                    <Download size={14} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download log range as CSV</p>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <AgGridReact
            className="rounded-lg"
            ref={gridRef}
            rowData={logs || []}
            columnDefs={Columns}
            theme={gridTheme}
            getRowId={(params) =>
              `line${params.data.sourceType}_${params.data.timestamp}_${params.data.message.length}_${params.data.instanceId}_${params.data.taskId}`
            }
            onRowDataUpdated={(event) =>
              event.api.autoSizeColumns({ colIds: ["message"] })
            }
            enableCellTextSelection
          />
        </div>
      </Stack>
    </div>
  );
};

const FilterField = ({
  filterPattern,
  setFilterPattern,
}: {
  filterPattern: string | undefined;
  setFilterPattern: (filterPattern: string | undefined) => void;
}) => {
  const [localFilterPattern, setLocalFilterPattern] = useState(filterPattern);
  const debouncedFilterPattern = useDebounce(localFilterPattern, 400);

  useEffect(() => {
    setFilterPattern(debouncedFilterPattern);
  }, [setFilterPattern, debouncedFilterPattern]);

  useEffect(() => {
    // Sync external filterPattern prop to local state
    // localFilterPattern is intentionally omitted from dependencies
    // to prevent infinite loop
    if (filterPattern !== localFilterPattern) {
      setLocalFilterPattern(filterPattern);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPattern]);

  return (
    <div className="relative flex-grow pr-4">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
      <Input
        placeholder="Filter events"
        value={localFilterPattern}
        className="pl-10"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setLocalFilterPattern(e.target.value || "");
        }}
      />
    </div>
  );
};

const inRange = (timestamp: number, timeRange?: TimeRange) => {
  if (!timeRange) {
    return true;
  }
  return timestamp >= timeRange[0] && timestamp <= timeRange[1];
};

// from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
const escapeRegExp = (s: string) => {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

const dedupeLogs = (logs: LogEntry[]) => {
  const seen = new Set<string>();
  return logs.filter((log) => {
    const key = `${log.timestamp}${log.instanceId}${log.message}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

// Filter logs by filter pattern and time range
// TODO: Make this less loopy
const filterLogs = (
  logs: LogEntry[],
  instanceLabelMap: Map<number | string, string>,
  filterPattern: string,
  filterTimeRange: boolean,
  timeRange?: TimeRange,
) => {
  const logsWithId = logs.map((log, id) => ({ ...log, id }));
  if (!filterPattern) {
    return logsWithId.filter(
      (log) => !filterTimeRange || inRange(log.timestamp, timeRange),
    );
  }

  // if the filter string is "instance:123 foo", then match "123" on the instance and "foo" on the message
  const filters = filterPattern.split(" ");

  const instanceFilters = filters
    .filter((value) => value.startsWith("instance:"))
    .map((value) => value.replace("instance:", ""));
  const taskFilters = filters
    .filter((value) => value.startsWith("task:"))
    .map((value) => value.replace("task:", ""));
  const nonInstancePattern = filters
    .filter(
      (value) => !value.startsWith("instance:") && !value.startsWith("task:"),
    )
    .join(" ");
  const literalRegex = new RegExp(escapeRegExp(nonInstancePattern), "i");

  let regexRegex: RegExp | undefined;
  try {
    regexRegex = new RegExp(nonInstancePattern, "i");
  } catch {
    // the filter string isn't a valid regex, for example "[foo"
  }

  const filteredLogs = logsWithId.filter(
    (log) =>
      // match on message
      (literalRegex.test(log.message) ||
        (regexRegex && regexRegex.test(log.message)) ||
        // or on instance IP address
        (nonInstancePattern.includes(".") &&
          log.instanceId &&
          instanceLabelMap.get(log.instanceId) &&
          literalRegex.test(instanceLabelMap.get(log.instanceId) || "")) ||
        // or on state
        (log.state &&
          (literalRegex.test(log.state) ||
            (regexRegex && regexRegex.test(log.state))))) &&
      // and on instance if there's an instance filter
      (!instanceFilters.length ||
        (log.instanceId &&
          instanceLabelMap.get(log.instanceId) &&
          instanceFilters.includes(
            instanceLabelMap.get(log.instanceId) || "",
          ))) &&
      // and on task if there's a task filter
      (!taskFilters.length ||
        (typeof log.taskId === "number" &&
          taskFilters.includes(log.taskId.toString()))) &&
      // and in time range if range is specified
      (!filterTimeRange || inRange(log.timestamp, timeRange)),
  );
  return filteredLogs;
};
